import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query'

import {
  AddConfigurationResponse,
  Configuration,
  ConfigurationNameAvailableResponse,
  Configurations,
} from '../../common/types'
import { useInternalClient } from '../clients/InternalClient'
import { useInternalConfigClient } from '../clients/InternalConfigClient'
import {
  MutationKeys,
  QueryKeys,
  ReactQueryMutationHooks,
  useMutationOptions,
} from '../types/local/general'
import { RpcStatus } from '../types/local/tenant'

export const useInternalConfig = (
  options?: Omit<
    UseQueryOptions<Configurations, RpcStatus, Configurations, QueryKeys[]>,
    'queryKey' | 'queryFn' | 'retry' | 'staleTime'
  >
) => {
  const { get } = useInternalConfigClient()

  return useQuery([QueryKeys.InternalConfig], () => get<Configurations>({ path: 'config' }), {
    ...options,
    enabled: options?.enabled,
    retry: false,
    staleTime: Infinity,
  })
}

export const useConfiguration = (
  options?: Omit<
    UseQueryOptions<Configurations, RpcStatus, Configurations, QueryKeys[]>,
    'queryKey' | 'queryFn' | 'retry' | 'staleTime'
  >
) => {
  const { get } = useInternalClient()

  return useQuery([QueryKeys.Configurations], () => get<Configurations>({ path: 'config' }), {
    ...options,
    enabled: options?.enabled,
    retry: false,
    staleTime: Infinity,
  })
}

export const useAddConfiguration = (
  reactQueryHooks: ReactQueryMutationHooks<Configuration, unknown> = {}
) => {
  const mutationOptions = useMutationOptions(reactQueryHooks)
  const { post } = useInternalClient()
  const queryClient = useQueryClient()

  return useMutation(
    [MutationKeys.AddConfiguration],
    (body) =>
      post({
        body,
        path: `config`,
      }),
    {
      ...mutationOptions,
      onSuccess: (data, context) => {
        queryClient.invalidateQueries([QueryKeys.Configurations])
        queryClient.removeQueries([QueryKeys.IsConfigurationNameAvailable, data.name])
        mutationOptions?.onSuccess?.(data, context)
      },
    }
  )
}

export const useUpdateConfiguration = (
  mutationHooks: ReactQueryMutationHooks<
    AddConfigurationResponse,
    { name: string; fields: Configuration }
  > = {}
) => {
  const mutationOptions = useMutationOptions(mutationHooks)

  const { put } = useInternalClient()
  const queryClient = useQueryClient()

  return useMutation(
    [MutationKeys.UpdateConfiguration],
    ({ name, ...data }) => put({ body: data.fields, path: `config/${name}` }),
    {
      ...mutationOptions,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries([QueryKeys.Configurations])
        queryClient.removeQueries([QueryKeys.IsConfigurationNameAvailable, variables.name])
        mutationOptions?.onSuccess?.(data, variables, context)
      },
    }
  )
}

export const useRemoveConfiguration = (
  mutationHooks: ReactQueryMutationHooks<unknown, string> = {}
) => {
  const queryClient = useQueryClient()
  const mutationOptions = useMutationOptions(mutationHooks, [QueryKeys.PoliciesData])
  const { del } = useInternalClient()
  return useMutation((name) => del<unknown>({ path: `config/${name}` }), {
    ...mutationOptions,
    onSuccess: (data, context) => {
      queryClient.removeQueries([QueryKeys.Configurations])
      mutationOptions?.onSuccess?.(data, context)
    },
  })
}

export const useIsConfigurationInstanceNameAvailable = (
  configName: string,
  options?: Omit<
    UseQueryOptions<
      ConfigurationNameAvailableResponse,
      RpcStatus,
      ConfigurationNameAvailableResponse,
      QueryKeys[]
    >,
    'queryKey' | 'queryFn' | 'retry' | 'staleTime'
  >
) => {
  const { get } = useInternalClient()
  return useQuery(
    [QueryKeys.IsConfigurationNameAvailable, configName as QueryKeys],
    () =>
      get<ConfigurationNameAvailableResponse>({
        path: `config/available/${configName}`,
      }),
    {
      ...options,
      enabled: !!configName && options?.enabled,
      retry: false,
      staleTime: Infinity,
    }
  )
}
